import {
  ALPHABET,
  ALPHABET_SIZE,
  foldDiacritics,
  letterIndex,
  mapLetters,
} from "@/lib/tool-kit/ciphers/alphabet";

/**
 * Atbash: the alphabet read backwards. A ↔ Z, B ↔ Y … M ↔ N.
 *
 * There is no key, and the cipher is its own inverse — enciphering twice returns the
 * original — so a widget needs one action, not an encode/decode switch. In affine terms
 * it is E(x) = (25x + 25) mod 26.
 */

const LAST_INDEX = ALPHABET_SIZE - 1;

/** Index i (0–25) becomes 25 − i. */
export function atbashIndex(index: number): number {
  return LAST_INDEX - index;
}

/** Mirror every A–Z letter, keeping its case; everything else passes through. */
export function atbash(text: string): string {
  return mapLetters(text, atbashIndex);
}

/** The thirteen swaps that make up the whole cipher: A–Z, B–Y … M–N. */
export const ATBASH_PAIRS: readonly (readonly [string, string])[] = Array.from(
  { length: ALPHABET_SIZE / 2 },
  (_, index) =>
    [ALPHABET.charAt(index), ALPHABET.charAt(LAST_INDEX - index)] as const
);

export interface AtbashStep {
  letter: string;
  /** 1-based: A = 1. */
  position: number;
  /** 27 − position. */
  mirrored: number;
  result: string;
}

/** The working for the first `limit` letters, in 1-based positions. */
export function atbashSteps(text: string, limit: number): AtbashStep[] {
  const steps: AtbashStep[] = [];
  for (const char of foldDiacritics(text)) {
    if (steps.length >= limit) {
      break;
    }
    const index = letterIndex(char);
    if (index < 0) {
      continue;
    }
    steps.push({
      letter: char.toUpperCase(),
      position: index + 1,
      mirrored: ALPHABET_SIZE - index,
      result: ALPHABET.charAt(atbashIndex(index)),
    });
  }
  return steps;
}
